// Branch.io Deep Linking Integration
import branch from 'branch-sdk';
import { getString } from 'firebase/remote-config';
import { remoteConfig } from './firebase';
import { getDeal } from './dealRepository';
import { Deal, dealToMap } from '../types/deal';
import { prefetchDealAssets } from '../utils/storageUrl';
import { applyDealDefaultLocale, getLocale, isUserChosenLocale } from '../i18n';

let branchInitialized = false;

/**
 * Deal/Promotion data from Branch deep link
 * Simplified to match Flutter implementation - only stores deal_id
 */
export interface BranchDealData {
  dealId: string;
  campaign?: string;
  channel?: string;
  feature?: string;
  tags?: string[];
  [key: string]: any;
}

const BRANCH_DEAL_STORAGE_KEY = 'branch_pending_deal';
const BRANCH_DEAL_DOC_STORAGE_KEY = 'branch_pending_deal_doc';
const BRANCH_DATA_STORAGE_KEY = 'branch_link_data';

/**
 * Fired on `window` whenever the pending deal document changes (saved or
 * cleared). Promo pages listen for this so they pick up a deal that arrives
 * asynchronously from Branch + Firestore, instead of only reading once on mount.
 */
export const PENDING_DEAL_EVENT = 'urunn:pending-deal-updated';

const emitPendingDealUpdated = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PENDING_DEAL_EVENT));
  }
};

/**
 * Fast-path: if the URL carries a `deal` id (e.g. /signup?deal=DEAL123), fetch
 * and apply the deal immediately on boot — in parallel with Branch init — so the
 * deal + its images load without waiting for the Branch match round-trip.
 * No-op when the param is absent.
 */
export const loadDealFromUrl = async (): Promise<void> => {
  try {
    const dealId = new URLSearchParams(window.location.search).get('deal');
    if (!dealId) return;
    console.log('[Branch] ⚡ deal param in URL, fetching immediately:', dealId);
    const deal = await getDeal(dealId);
    if (deal) {
      savePendingDealDoc(deal);
    } else {
      console.warn('[Branch] deal param did not resolve to a deal:', dealId);
    }
  } catch (error) {
    console.error('[Branch] loadDealFromUrl error:', error);
  }
};

/**
 * Initialize Branch SDK
 * Note: Remote Config should be initialized before calling this function
 */
export const initializeBranch = async (): Promise<void> => {
  if (branchInitialized) {
    console.log('[Branch] Already initialized');
    return;
  }

  try {
    // Get Branch configuration from Remote Config (already initialized in App.tsx)
    const branchKey = getString(remoteConfig, 'BRANCH_API_KEY');

    console.log('[Branch] Retrieved Branch key from Remote Config, length:', branchKey?.length);

    if (!branchKey) {
      console.warn('[Branch] BRANCH_API_KEY not found in Remote Config');
      // Try environment variable as fallback
      const envKey = process.env.REACT_APP_BRANCH_KEY;
      if (envKey) {
        console.log('[Branch] Using Branch key from environment variables');
        branch.init(envKey, initCallback);
        branchInitialized = true;
        return;
      }
      
      console.warn('[Branch] Branch key not found in environment variables, cannot initialize');
      return;
    }

    console.log('[Branch] Initializing Branch with key from Remote Config');
    branch.init(branchKey, initCallback);
    branchInitialized = true;
  } catch (error) {
    console.error('[Branch] Error initializing Branch:', error);
  }
};

/**
 * Branch initialization callback
 */
const initCallback = (error: any, data: any) => {
  if (error) {
    console.error('[Branch] Initialization error:', error);
    return;
  }

  console.log('[Branch] Initialized successfully');
  console.log('[Branch] Deep link data:', data);

  // Check if this is a deep link click
  // Branch can return data in different structures, check both root and data_parsed
  const clickedBranchLink = data?.['+clicked_branch_link'] || data?.data_parsed?.['+clicked_branch_link'];
  console.log('[Branch] Clicked Branch link:', data?.data_parsed?.['+clicked_branch_link']);
  
  if (data && clickedBranchLink) {
    console.log('[Branch] User came from a Branch link!');
    // Use data_parsed if available, otherwise use root data
    const branchData = data.data_parsed || data;
    handleBranchData(branchData);
  } else {
    console.log('[Branch] Organic or direct traffic');
  }
};

/**
 * Handle Branch deep link data
 * Matches Flutter implementation - fetches deal from Firestore
 */
const handleBranchData = async (data: any) => {
  try {
    console.log('[Branch] Processing deep link data:', JSON.stringify(data, null, 2));

    // Save all Branch data for reference
    saveBranchData(data);

    // Store Everflow transaction ID if present (before deal handling,
    // since a deal link can also carry an Everflow transaction ID)
    const transId = data['transaction_id'] || data['trans_id'] || data['~click_id'];
    if (transId) {
      console.log('[Branch] Everflow transaction ID detected:', transId);
      localStorage.setItem('pending_everflow_tid', transId);
    }

    // Check if this is a deal link (has ~feature and deal_id)
    if (data['~feature'] && data['deal_id']) {
      const dealId = data['deal_id'];
      console.log('[Branch] ✅ Deal link detected!');
      console.log('[Branch] Fetching deal from Firestore: /deals/' + dealId);
      
      try {
        // Fetch deal from Firestore (matches Flutter's getDeal())
        const dealDoc = await getDeal(dealId);
        
        if (dealDoc) {
          console.log('[Branch] ✅ Deal fetched successfully!');
          console.log('[Branch] Deal details:', JSON.stringify({
            id: dealDoc.id,
            name: dealDoc.name,
            message: dealDoc.message,
            description: dealDoc.description,
            backgroundColor: dealDoc.backgroundColor,
            textColor: dealDoc.textColor,
            logo: dealDoc.logo
          }, null, 2));
          
          // Save deal document to cache
          savePendingDealDoc(dealDoc);
          
          // Also save minimal deal data
          const dealData: BranchDealData = {
            dealId: dealId,
            campaign: data['~campaign'] || data.campaign,
            channel: data['~channel'] || data.channel,
            feature: data['~feature'] || data.feature,
            tags: data['~tags'] || data.tags,
          };
          
          savePendingDeal(dealData);
        } else {
          console.error('[Branch] ❌ Deal not found in Firestore!');
          console.error('[Branch] Looked for deal at path: /deals/' + dealId);
          console.error('[Branch] Please create this deal in Firebase Console → Firestore Database');
        }
      } catch (error: any) {
        console.error('[Branch] ❌ Error fetching deal from Firestore!');
        console.error('[Branch] Error details:', error);
        console.error('[Branch] Error message:', error.message);
        console.error('[Branch] Make sure the deal exists at: /deals/' + dealId);
      }
    } else {
      console.log('[Branch] ℹ️  No deal data in the link');
      console.log('[Branch] Expected fields: ~feature="deals" and deal_id="DEAL_ID"');
      console.log('[Branch] Received:', { 
        feature: data['~feature'], 
        dealId: data['deal_id'] 
      });
    }
  } catch (error: any) {
    console.error('[Branch] ❌ Error handling Branch data:', error);
    console.error('[Branch] Error message:', error.message);
  }
};

/**
 * Save pending deal data to localStorage
 * This data will be applied after user logs in
 */
export const savePendingDeal = (dealData: BranchDealData): void => {
  try {
    localStorage.setItem(BRANCH_DEAL_STORAGE_KEY, JSON.stringify(dealData));
    console.log('[Branch] Pending deal saved:', dealData);
  } catch (error) {
    console.error('[Branch] Error saving pending deal:', error);
  }
};

/**
 * Save pending deal document to localStorage
 * Stores the full Deal object from Firestore
 */
export const savePendingDealDoc = (deal: Deal): void => {
  try {
    const dealMap = dealToMap(deal);
    localStorage.setItem(BRANCH_DEAL_DOC_STORAGE_KEY, JSON.stringify(dealMap));
    console.log('[Branch] Pending deal document saved:', deal.name);
    // Open in the deal's default language (unless the user already chose one)
    applyDealDefaultLocale(deal.defaultLanguage);
    // Warm the deal's images (resolve + browser-cache) before any page renders
    prefetchDealAssets(deal);
    emitPendingDealUpdated();
  } catch (error) {
    console.error('[Branch] Error saving pending deal document:', error);
  }
};

/**
 * Get pending deal data
 */
export const getPendingDeal = (): BranchDealData | null => {
  try {
    const data = localStorage.getItem(BRANCH_DEAL_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  } catch (error) {
    console.error('[Branch] Error getting pending deal:', error);
    return null;
  }
};

/**
 * Build a same-origin URL for `path`, preserving the active deal id as a
 * `?deal=` query (from the current URL or the pending deal doc). Used as the
 * post-verification continueUrl so users return to the co-branded deal page.
 */
export const dealAwareUrl = (path: string): string => {
  const origin = window.location.origin;
  const urlDeal = new URLSearchParams(window.location.search).get('deal');
  const dealId = urlDeal || getPendingDealDoc()?.id;
  const params = new URLSearchParams();
  if (dealId) params.set('deal', dealId);
  // Preserve the language only when the visitor actually chose it. Stamping a
  // locale we merely derived (from their device, or the deal's default) would
  // come back as an explicit ?lang= and pin it in localStorage for good — so a
  // deal's French default would follow an English device around forever.
  if (isUserChosenLocale()) params.set('lang', getLocale());
  const qs = params.toString();
  return `${origin}${path}${qs ? `?${qs}` : ''}`;
};

/**
 * Get pending deal document
 */
export const getPendingDealDoc = (): Deal | null => {
  try {
    const data = localStorage.getItem(BRANCH_DEAL_DOC_STORAGE_KEY);
    if (data) {
      const deal = JSON.parse(data) as Deal;
      // Open in the deal's default language (no-op if the user already chose one)
      applyDealDefaultLocale(deal.defaultLanguage);
      return deal;
    }
    return null;
  } catch (error) {
    console.error('[Branch] Error getting pending deal document:', error);
    return null;
  }
};

/**
 * Clear pending deal data after it's been applied
 */
export const clearPendingDeal = (): void => {
  try {
    localStorage.removeItem(BRANCH_DEAL_STORAGE_KEY);
    localStorage.removeItem(BRANCH_DEAL_DOC_STORAGE_KEY);
    console.log('[Branch] Pending deal cleared');
    emitPendingDealUpdated();
  } catch (error) {
    console.error('[Branch] Error clearing pending deal:', error);
  }
};

/**
 * Save all Branch data for analytics/reference
 */
const saveBranchData = (data: any): void => {
  try {
    // Store with timestamp
    const branchData = {
      data,
      timestamp: new Date().toISOString(),
      clickedBranchLink: data['+clicked_branch_link'],
    };
    
    localStorage.setItem(BRANCH_DATA_STORAGE_KEY, JSON.stringify(branchData));
    console.log('[Branch] Full Branch data saved');
  } catch (error) {
    console.error('[Branch] Error saving Branch data:', error);
  }
};

/**
 * Get all Branch data
 */
export const getBranchData = (): any | null => {
  try {
    const data = localStorage.getItem(BRANCH_DATA_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  } catch (error) {
    console.error('[Branch] Error getting Branch data:', error);
    return null;
  }
};

/**
 * Track a Branch event
 */
export const trackBranchEvent = (eventName: string, metadata?: Record<string, any>): void => {
  if (!branchInitialized) {
    console.warn('[Branch] Cannot track event - Branch not initialized');
    return;
  }

  try {
    branch.logEvent(eventName, metadata || {}, (error: any) => {
      if (error) {
        console.error('[Branch] Error tracking event:', error);
      } else {
        console.log('[Branch] Event tracked:', eventName, metadata);
      }
    });
  } catch (error) {
    console.error('[Branch] Error in trackBranchEvent:', error);
  }
};

/**
 * Track purchase event with Branch
 */
export const trackBranchPurchase = (
  revenue: number,
  currency: string,
  transactionId: string,
  additionalData?: Record<string, any>
): void => {
  const purchaseData = {
    revenue,
    currency,
    transaction_id: transactionId,
    ...additionalData,
  };

  trackBranchEvent('PURCHASE', purchaseData);
};

/**
 * Set Branch user identity
 */
export const setBranchIdentity = (userId: string): void => {
  if (!branchInitialized) {
    console.warn('[Branch] Cannot set identity - Branch not initialized');
    return;
  }

  try {
    branch.setIdentity(userId, (error: any, data: any) => {
      if (error) {
        console.error('[Branch] Error setting identity:', error);
      } else {
        console.log('[Branch] User identity set:', userId);
      }
    });
  } catch (error) {
    console.error('[Branch] Error in setBranchIdentity:', error);
  }
};

/**
 * Clear Branch user identity (on logout)
 */
export const clearBranchIdentity = (): void => {
  if (!branchInitialized) {
    return;
  }

  try {
    branch.logout((error: any) => {
      if (error) {
        console.error('[Branch] Error logging out:', error);
      } else {
        console.log('[Branch] User logged out from Branch');
      }
    });
  } catch (error) {
    console.error('[Branch] Error in clearBranchIdentity:', error);
  }
};

/**
 * TEST UTILITY: Simulate a Branch deep link with deal data
 * This is for testing purposes only - allows you to test the deal flow without creating a Branch link
 * 
 * Usage in browser console:
 *   import { testBranchDeal } from './branch';
 *   testBranchDeal('YOUR_DEAL_ID');
 * 
 * Or add to window for easy testing:
 *   (window as any).testBranchDeal = testBranchDeal;
 */
export const testBranchDeal = async (dealId: string): Promise<void> => {
  console.log('[Branch TEST] Simulating Branch link click with deal_id:', dealId);
  
  const testData = {
    '+clicked_branch_link': true,
    '~feature': 'deals',
    'deal_id': dealId,
    '~campaign': 'test_campaign',
    '~channel': 'test',
  };
  
  await handleBranchData(testData);
  console.log('[Branch TEST] Test complete. Check localStorage for pending deal.');
};

// Expose to window for easy testing in console
if (typeof window !== 'undefined') {
  (window as any).testBranchDeal = testBranchDeal;
  
  // Add helper to check current state
  (window as any).checkBranchState = () => {
    console.log('=== Branch State ===');
    console.log('Initialized:', branchInitialized);
    console.log('Pending deal:', getPendingDeal());
    console.log('Pending deal doc:', getPendingDealDoc());
    console.log('All Branch data:', getBranchData());
    console.log('==================');
  };
}
